import createHistory from 'history/createBrowserHistory';

import api from 'utils/api';
import { gettext } from 'utils/text';
import { buildUrl } from 'utils/urls';

const history = createHistory();

const LOAD_ITEMS = 'arena/LOAD_ITEMS';
const SET_CHOICES = 'arena/SET_CHOICES';
const SET_SEARCH = 'arena/SET_SEARCH';
const SET_ORDERING = 'arena/SET_ORDERING';
const SET_FILTER_KEY = 'arena/SET_FILTER_KEY';
const SET_FILTERS = 'arena/SET_FILTERS';
const SET_URL_HANDLING = 'arena/SET_URL_HANDLING';
const SET_FAVOURITES = 'arena/SET_FAVOURITES';
const SET_FAVOURITE_STATUS = 'arena/SET_FAVOURITE_STATUS';
const SET_MEDIA_IN_PROJECT_STATUS = 'arena/SET_MEDIA_IN_PROJECT_STATUS';
const SET_CACHE_ITEM = 'arena/SET_CACHE_ITEM';

export const ORDERING_CHOICES = {
    choices: [
        { value: 'latest', label: gettext('Latest'), apiKey: '-media__created_at' },
        { value: 'newandpopular', label: gettext('New and popular'), apiKey: '-new_and_popular_weight' },
        { value: 'mostcommented', label: gettext('Most commented'), apiKey: '-number_of_public_comments' },
        { value: 'lastcommented', label: gettext('Last commented'), apiKey: '-last_public_comment__created_at' },
    ],
    default: 'latest',
};

const initialState = {
    items: {},
    itemIds: [],
    // pageSpecificItems do not render in masonry, but are updated when favourites change and provide data
    // to components on the page outside masonry
    pageSpecificItems: {},
    limit: 6,
    choices: [{ key: 'all', filter: {}, label: 'All' }],
    filters: {},
    filterKey: 'all',
    hasMore: true,
    startLoad: true,
    query: '',
    ordering: ORDERING_CHOICES.default,
    // Some places across trafo, get parameters are used for various stuff, so can't really change the URL
    // without harm done, thus adding an option to skip this
    handleUrl: true,
    favouriteItemList: [],
};

export default function arenaReducer(state = initialState, action) {
    switch (action.type) {
        case LOAD_ITEMS: {
            const normalizedItems = {};
            const itemIds = action.clear ? [] : [...state.itemIds];

            action.items.forEach((item) => {
                normalizedItems[item.id] = item;

                if (!itemIds.includes(item.id)) {
                    itemIds.push(item.id);
                }
            });

            const items = action.clear ? normalizedItems : { ...state.items, ...normalizedItems };

            return {
                ...state,
                hasMore: action.count > itemIds.length,
                items,
                itemIds,
                startLoad: false,
            };
        }

        case SET_CACHE_ITEM: {
            return {
                ...state,
                pageSpecificItems: action.pageSpecificItems,
                mediaCacheId: action.mediaCacheId,
            };
        }

        case SET_SEARCH: {
            return {
                ...state,
                query: action.query,
                startLoad: true,
            };
        }

        case SET_ORDERING: {
            return {
                ...state,
                ordering: action.ordering,
                startLoad: true,
            };
        }

        case SET_FILTER_KEY: {
            return {
                ...state,
                filterKey: action.filterKey,
                ordering: ORDERING_CHOICES.choices.map((k) => k.value).includes(state.ordering)
                    ? state.ordering
                    : ORDERING_CHOICES.default,
                startLoad: true,
            };
        }

        case SET_FILTERS: {
            return {
                ...state,
                ...action.filters,
                startLoad: true,
            };
        }

        case SET_CHOICES: {
            return {
                ...state,
                choices: action.choices,
                filterKey: action.filterKey,
            };
        }

        case SET_URL_HANDLING: {
            return {
                ...state,
                handleUrl: action.handle,
            };
        }

        case SET_FAVOURITES: {
            return {
                ...state,
                favouriteItemList: action.favouriteItemList,
            };
        }

        case SET_FAVOURITE_STATUS: {
            let changedItems = {};
            if (action.mediaCacheId in state.items) {
                changedItems = { ...action.items };
            }

            return {
                ...state,
                // Either filter the id out, or add it
                favouriteItemList: action.status
                    ? state.favouriteItemList.concat([action.mediaCacheId])
                    : state.favouriteItemList.filter((mediaCacheId) => mediaCacheId !== action.mediaCacheId),
                items: { ...state.items, ...changedItems },
                pageSpecificItems: { ...state.pageSpecificItems, ...action.items },
            };
        }

        case SET_MEDIA_IN_PROJECT_STATUS: {
            const { mediaCacheId, projectId, isInProject } = action;
            if (!(mediaCacheId in state.items)) {
                return { ...state };
            }

            const filteredProjects = state.items[mediaCacheId].projects.filter((id) => id !== projectId);

            return {
                ...state,
                items: {
                    ...state.items,
                    [mediaCacheId]: {
                        ...state.items[mediaCacheId],
                        projects: isInProject ? [...filteredProjects, projectId] : filteredProjects,
                    },
                },
            };
        }

        default: {
            return state;
        }
    }
}

const setItems = (items, count, clear) => ({ type: LOAD_ITEMS, items, count, clear });

const makeUrl = (state) => {
    const { filterKey, query, ordering } = state;

    const baseUrl = window.location.pathname;

    // Query parameters to be added to the URL.
    // The keys are parameter names, values are parameter values.
    const queryParams = {
        filter: filterKey,
        query,
        ordering,
    };

    return buildUrl(baseUrl, queryParams);
};

export const selectActiveFilters = ({ choices, filterKey }) =>
    choices.find((filter) => filter.key === filterKey) || choices[0];

export const loadItems = (clear = false) => (dispatch, getState) => {
    const state = getState();
    const { itemIds, limit, query, ordering, handleUrl } = state;

    const activeFilters = selectActiveFilters(state);
    const apiKey = activeFilters['apiKey'] || 'arena';

    const queryParams = {
        offset: clear ? 0 : itemIds.length,
        limit,
        query,
        ...activeFilters.filter,
        ordering: ORDERING_CHOICES.choices.find((k) => k.value === ordering).apiKey,
    };

    api[apiKey].fetch(null, queryParams).then((result) => {
        dispatch(setItems(result.results, result.count, clear));
        if (handleUrl) {
            const url = makeUrl(state);
            history.replace(url);
        }
    });
};

export const loadPageSpecificItems = (mediaId) => (dispatch) => {
    api.arena
        .fetch(null, {
            media: mediaId,
        })
        .then((result) => {
            const pageItem = result.results[0];
            dispatch({
                type: SET_CACHE_ITEM,
                pageSpecificItems: {
                    [pageItem.id]: pageItem,
                },
                mediaCacheId: pageItem.id,
            });
        });
};

export const search = (query) => (dispatch) => {
    dispatch({ type: SET_SEARCH, query });
    dispatch(loadItems(true));
};

export const sort = (ordering) => (dispatch) => {
    dispatch({ type: SET_ORDERING, ordering });
    dispatch(loadItems(true));
};

export const setFilterKey = (filterKey) => (dispatch) => {
    dispatch({ type: SET_FILTER_KEY, filterKey });
    dispatch(loadItems(true));
};

/* Used to initially set filters when the app loads form URL with filters prefilled */
export const setFilters = (filters) => (dispatch) => {
    dispatch({ type: SET_FILTERS, filters });
    dispatch(loadItems(true));
};

export const setChoices = (choiceInformation) => (dispatch) => {
    dispatch({ type: SET_CHOICES, ...choiceInformation });
    dispatch(loadItems(true));
};

export const setUrlHandling = (handle) => (dispatch) => {
    dispatch({ type: SET_URL_HANDLING, handle });
};

export const setFavourites = (favouriteItemList) => (dispatch) => {
    dispatch({ type: SET_FAVOURITES, favouriteItemList });
};

export const setFavouriteStatus = ({ mediaCacheId, status, items }) => (dispatch) => {
    dispatch({ type: SET_FAVOURITE_STATUS, mediaCacheId, status, items });
};

export const toggleFavourite = (mediaCacheId) => (dispatch) => {
    api.toggleFavourite.post({ mediaCacheId }).then((result) => {
        if (result.success) {
            dispatch(setFavouriteStatus({ mediaCacheId, status: result.new_favourite_status, items: result.items }));
        }
    });
};

export const addOrRemoveMediaForProject = ({ item, projectId, isInProject }) => (dispatch) => {
    api.projectMedia
        .put({ pk: projectId }, { [isInProject ? 'add_media' : 'remove_media']: [item.media] })
        .then((result) => {
            dispatch({ type: SET_MEDIA_IN_PROJECT_STATUS, mediaCacheId: item.id, isInProject, projectId });
            // Filters might cause the item to disappear in new state - trigger reloading for this to work
            dispatch(loadItems(true));
        });
};

export const removeMediaFromProject = ({ item, projectId }) => (dispatch) => {
    dispatch(addOrRemoveMediaForProject({ item, projectId, isInProject: false }));
};

export const addMediaToProject = ({ item, projectId }) => (dispatch) => {
    dispatch(addOrRemoveMediaForProject({ item, projectId, isInProject: true }));
};

export const loadFavourites = () => (dispatch) => {
    api.favourites.fetch().then((result) => {
        dispatch(setFavourites(result.favourites));
    });
};



// WEBPACK FOOTER //
// ./src/ducks/arena.js